import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Chart } from 'chart.js/auto';
import { ReportService } from 'src/app/dashboard/shared/services/report.service';
import { ReportingService } from 'src/app/reporting/shared/services/reporting.service';
import { EnergyGraphData } from '../../interfaces/energy-graph-data.interface';

@Component({
  selector: 'avs-voltage-chart',
  templateUrl: './voltage-chart.component.html',
  styleUrls: ['./voltage-chart.component.css']
})
export class VoltageChartComponent implements OnInit, OnDestroy {

  public samId: number;
  public deviceName: string = "";
  public now = new Date();
  public nowAsISOString: string;
  public twoDaysAgoAsString: string;

  public fromDate: number = 2;
  public toDate: number = 0;
  public dataAge: number = 0;
  public fontSize: number = 10;
  private chartIds = ['VoltageChart', 'CurrentChart', 'PowerChart', 'ErrorChart'];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { samId: number, deviceName: string, solarDateTime?: string },
    private reportService: ReportService,
    private reportingService: ReportingService,
    private dialogRef: MatDialogRef<VoltageChartComponent>,
  ) {
    const lastData = new Date(this.data.solarDateTime ?? NaN);
    const today = new Date();
    const diff = today.getTime() - lastData.getTime();
    const diffDays = diff / (1000 * 60 * 60 * 24);
    this.dataAge = diffDays;

    if (this.dataAge !== undefined && this.dataAge > 1) {
      this.toDate = Math.round(this.dataAge);
      this.fromDate = Math.round(this.dataAge) + 2;
    }

    this.samId = this.data.samId;
    this.deviceName = this.data.deviceName;
    this.nowAsISOString = this.now.toISOString();

    const d = new Date();
    d.setDate(d.getDate() - 2);
    this.twoDaysAgoAsString = d.toISOString();
  }

  ngOnDestroy(): void {
    // Chart.js-Observer freigeben, sonst akkumulieren dokumentweite MutationObserver
    this.chartIds.forEach(id => Chart.getChart(id)?.destroy());
  }

  ngOnInit(): void {
    this.getData();
  }

  private getData(): void {
    this.reportingService.getEnergyGraphics(
      { utcFrom: this.twoDaysAgoAsString, utcTo: this.nowAsISOString, samId: this.samId }
    ).subscribe((data: EnergyGraphData[]) => {
      this.destroyCharts();
      this.createVoltageChart(data);
      this.createCurrentChart(data);
      this.createPowerChart(data);
      this.createErrorChart(data);
    });
  }

  private destroyCharts(): void {
    this.chartIds.forEach(id => {
      const chartInstance = Chart.getChart(id);
      if (chartInstance) {
        chartInstance.destroy();
      }
    });
  }

  // 1) VOLTAGE: batteryVoltage & loadVoltage
  private createVoltageChart(data: EnergyGraphData[]): void {
    const labels = data.map(d => d.time);
    const batteryVolt = data.map(d => d.batteryVoltage);
    const loadVolt = data.map(d => d.loadVoltage);

    new Chart('VoltageChart', {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Batteriespannung [V]',
            data: batteryVolt,
            borderColor: 'blue',
            backgroundColor: 'rgba(0,0,255,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,

            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: 'Lastspannung [V]',
            data: loadVolt,
            borderColor: 'orange',
            backgroundColor: 'rgba(255,165,0,0.1)',
            fill: false,

            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,

/*             borderDash: [3, 6],
 */          }
        ]
      },
      options: {
        aspectRatio: 2.5,
/*         parsing: false,
 */        scales: {
          x: {
            type: 'time',
            time: {
              // Parser entfernt – ISO wird direkt erkannt
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: { display: true } /* , text: 'Zeit'  */
          },
          y: {
            title: { display: true, text: 'Spannung [V]' }
          }
        },
        plugins: {
          legend: {
            labels: {
              font: {
                size: this.fontSize
              }
            }
          }
        }
      }
    });
  }

  // 2) CURRENT: batteryCurrent1/2 & loadCurrent1/2
  private createCurrentChart(data: EnergyGraphData[]): void {
    const labels = data.map(d => d.time);
    const batteryCurrent1 = data.map(d => d.batteryCurrent1);
    const batteryCurrent2 = data.map(d => d.batteryCurrent2 ?? NaN);
    const loadCurrent1 = data.map(d => d.loadCurrent1);
    const loadCurrent2 = data.map(d => d.loadCurrent2 ?? NaN);

    new Chart('CurrentChart', {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Batteriestrom 1 [A]',
            data: batteryCurrent1,
            borderColor: 'green',
            backgroundColor: 'rgba(0,255,0,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: 'Batteriestrom 2 [A]',
            data: batteryCurrent2,
            borderColor: 'darkgreen',
            backgroundColor: 'rgba(0,128,0,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: batteryCurrent2.every(v => Number.isNaN(v)),
          },
          {
            label: 'Laststrom 1 [A]',
            data: loadCurrent1,
            borderColor: 'purple',
            backgroundColor: 'rgba(128,0,128,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: 'Laststrom 2 [A]',
            data: loadCurrent2,
            borderColor: 'magenta',
            backgroundColor: 'rgba(255,0,255,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: loadCurrent2.every(v => Number.isNaN(v)),
          }
        ]
      },
      options: {
        aspectRatio: 2.5,
/*         parsing: false,
 */        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: { display: true } /* , text: 'Zeit'  */
          },
          y: {
            title: { display: true, text: 'Strom [A]' }
          }
        },
        plugins: {
          legend: {
            labels: {
              font: {
                size: this.fontSize
              }
            }
          }
        }
      }
    });
  }

  // 3) POWER: solarPower1 & solarPower2?
  private createPowerChart(data: EnergyGraphData[]): void {
    const labels = data.map(d => d.time);
    const power1 = data.map(d => d.solarPower1);
    const power2 = data.map(d => d.solarPower2 ?? NaN);

    new Chart('PowerChart', {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Solarleistung 1 [W]',
            data: power1,
            borderColor: 'orange',
            backgroundColor: 'rgba(255,165,0,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderDash: [6, 3],
            pointStyle: 'rectRounded',
          },
          {
            label: 'Solarleistung 2 [W]',
            data: power2,
            borderColor: 'red',
            backgroundColor: 'rgba(255,0,0,0.1)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: power2.every(v => Number.isNaN(v)),
          }
        ]
      },
      options: {
        aspectRatio: 2.5,
/*         parsing: false,
 */        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },

            },

            title: { display: true } /* , text: 'Zeit'  */
          },
          y: {
            title: { display: true, text: 'Leistung [W]' }
          }
        },
        plugins: {
          legend: {
            labels: {
              font: {
                size: this.fontSize
              }
            }
          }
        }
      }
    });
  }

  // 4) ERROR/WARN: 6 Linien
  private createErrorChart(data: EnergyGraphData[]): void {
    const labels = data.map(d => d.time);
    const errorWarn1 = data.map(d => d.errorWarnState1);
    const errorWarn2 = data.map(d => d.errorWarnState2 ?? NaN);
    const warnCode1 = data.map(d => d.warnCode1);
    const warnCode2 = data.map(d => d.warnCode2 ?? NaN);
    const errorCode1 = data.map(d => d.errorCode1);
    const errorCode2 = data.map(d => d.errorCode2 ?? NaN);

    new Chart('ErrorChart', {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Error/Warn State 1',
            data: errorWarn1,
            borderColor: 'red',
            backgroundColor: 'rgba(255,0,0,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,

          },
          {
            label: 'Error/Warn State 2',
            data: errorWarn2,
            borderColor: 'darkred',
            backgroundColor: 'rgba(139,0,0,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: errorWarn2.every(v => Number.isNaN(v)),
          },
          {
            label: 'WarnCode 1',
            data: warnCode1,
            borderColor: 'orange',
            backgroundColor: 'rgba(255,165,0,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
          },
          {
            label: 'WarnCode 2',
            data: warnCode2,
            borderColor: 'darkorange',
            backgroundColor: 'rgba(255,140,0,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: warnCode2.every(v => Number.isNaN(v)),
          },
          {
            label: 'ErrorCode 1',
            data: errorCode1,
            borderColor: 'purple',
            backgroundColor: 'rgba(128,0,128,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
          },
          {
            label: 'ErrorCode 2',
            data: errorCode2,
            borderColor: 'magenta',
            backgroundColor: 'rgba(255,0,255,0.15)',
            fill: false,
            tension: 0.1,
            borderWidth: 1,
            pointRadius: 2,
            pointHoverRadius: 4,
            hidden: errorCode2.every(v => Number.isNaN(v)),
          }
        ]
      },
      options: {
        aspectRatio: 2.5,
/*         parsing: false,
 */        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: { display: true } /* , text: 'Zeit'  */
          },
          y: {
            title: { display: true, text: 'Codes / States' }
          }
        },
        plugins: {
          legend: {
            labels: {
              font: {
                size: this.fontSize
              }
            }
          }
        }
      }
    });
  }

  public removeData(): void {
    this.destroyCharts();
    this.getData();
  }

  public setFromDate(date: number): void {
    this.fromDate = date;
    const d = new Date();
    d.setDate(d.getDate() - this.fromDate);
    this.twoDaysAgoAsString = d.toISOString();
  }

  public setToDate(date: number): void {
    this.toDate = date;
    const d = new Date();
    d.setDate(d.getDate() - this.toDate);
    this.nowAsISOString = d.toISOString();
  }

  public onCloseDialogClicked(): void {
    this.dialogRef.close();
  }

  public exportChart(): void {
    var allCanvas: HTMLCanvasElement[] = [];
    var height: number = 0;
    var width: number = 0;
    this.chartIds.forEach(id => {
      let canvas = document.getElementById(id) as HTMLCanvasElement;
      height += (canvas.height+20);
      width = canvas.width;
      allCanvas.push(canvas);
    });
    
    const combinedCanvas = document.createElement('canvas');
    combinedCanvas.width = width;
    combinedCanvas.height = height - 20;

    const ctx = combinedCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);
      var x: number = 0;
      var currentY: number = 0;
      allCanvas.forEach(c=> {
        ctx.drawImage(c, x, currentY);
        currentY += (c.height+20);
      });

    let dataURL = combinedCanvas.toDataURL('image/png');
    let link = document.createElement('a');
    link.href = dataURL;
    link.download = `energie-verlauf-${this.samId}-${new Date().toISOString().split('T')[0]}.png`;
    link.click();
    }
  }
}
